// src/store/thunks/connection/reconnectPeer.ts

import { multiaddr } from '@multiformats/multiaddr';

import { createAppAsyncThunk } from '../createAppAsyncThunk';

interface ReconnectPeerParams {
  sixId: string;
  peerId: string;
}

/**
 * Reconnect to a specific peer in a Six's P2P network via relay circuit.
 * Attempts to dial the peer through the configured relay server.
 */
export const reconnectPeer = createAppAsyncThunk(
  'connection/reconnectPeer',
  async(
    { sixId, peerId }: ReconnectPeerParams,
    { getState, extra: { pocServices, loggerService }, rejectWithValue }
  ) => {
    const log = loggerService.createLogger({ namespace: 'six3ro:p2p:connection' });
    const { p2pStackManagerService } = pocServices;

    try {
      const libp2pNode = p2pStackManagerService.getLibp2pNode(sixId);

      if (libp2pNode === null) {
        throw new Error(`No LibP2P node found for six '${sixId}'`);
      }
      const six = getState().sixCatalog.sixes.find(six => six.id === sixId);
      if(six === undefined) {
        throw new Error(`No Six found with id '${sixId}'`);
      }

      // Use the first available relay server to dial the peer via circuit
      const bootstrapNodes = six.bootstrapNodes ?? [];
      if (bootstrapNodes.length === 0) {
        throw new Error(`No bootstrap nodes configured for six '${sixId}'`);
      }

      // Try each bootstrap node until one succeeds
      let lastError: Error | null = null;
      for (const relayAddr of bootstrapNodes) {
        try {
          const circuitAddr = `${relayAddr}/p2p-circuit/p2p/${peerId}`;
          log.info(`Attempting to reconnect to peer ${peerId}... via relay ${relayAddr}`);

          const ma = multiaddr(circuitAddr);
          await libp2pNode.dial(ma);

          log.info(`Successfully reconnected to peer ${peerId}... via relay`);

          return {
            sixId,
            peerId,
            success: true,
          };
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          log.warn(`Failed to reconnect via ${relayAddr}: ${lastError.message}`);
        }
      }

      // If all relays failed, throw the last error
      throw lastError ?? new Error('All relay connection attempts failed');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      log.error(`Failed to reconnect to peer ${peerId}:`, errorMessage);
      return rejectWithValue({ sixId, peerId, message: errorMessage });
    }
  }
);
